import React, { useState } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { CalendarPicker } from '@appkit4/react-components/datepicker';
import Modal from '../../components/reusables/Modal';

interface AddProjectModalProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (data: {
        projectRef: string;
        projectName: string;
        projectDescription: string;
        contractor: string;
        contractDate: string;
    }) => void;
    isSubmitting?: boolean;
}

const AddProjectModal: React.FC<AddProjectModalProps> = ({
    visible,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const [projectRef, setProjectRef] = useState('');
    const [projectName, setProjectName] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    const [contractor, setContractor] = useState('');
    const [contractDate, setContractDate] = useState<string>('');

    const resetForm = () => {
        setProjectRef('');
        setProjectName('');
        setProjectDescription('');
        setContractor('');
        setContractDate('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = () => {
        onSubmit({
            projectRef,
            projectName,
            projectDescription,
            contractor,
            contractDate,
        });
    };

    const isFormValid =
        projectRef.trim() !== '' &&
        projectName.trim() !== '' &&
        contractor.trim() !== '' &&
        contractDate !== '';

    return (
        <Modal
            visible={visible}
            title="Add project"
            onClose={handleClose}
            modalStyle={{ width: '600px' }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: '0 0 35%' }}>
                        <Input
                            title="Project ID"
                            value={projectRef}
                            onChange={(value: string) => setProjectRef(value)}
                            required
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Input
                            title="Project name"
                            value={projectName}
                            onChange={(value: string) => setProjectName(value)}
                            required
                        />
                    </div>
                </div>

                <Input
                    title="Description"
                    value={projectDescription}
                    onChange={(value: string) => setProjectDescription(value)}
                />

                <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                        <Input
                            title="Main Contractor"
                            value={contractor}
                            onChange={(value: string) => setContractor(value)}
                            required
                        />
                    </div>
                    <div style={{ flex: '0 0 40%' }}>
                        <CalendarPicker
                            fieldTitle="Contract Date"
                            format="YYYY-MM-DD"
                            {...(contractDate !== '' && {
                                value: contractDate,
                            })}
                            onChange={(value: any) => {
                                if (value instanceof Date) {
                                    const yyyy = value.getFullYear();
                                    const mm = String(
                                        value.getMonth() + 1
                                    ).padStart(2, '0');
                                    const dd = String(value.getDate()).padStart(
                                        2,
                                        '0'
                                    );
                                    setContractDate(`${yyyy}-${mm}-${dd}`);
                                } else {
                                    setContractDate(value as string);
                                }
                            }}
                        />
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        marginTop: '8px',
                    }}
                >
                    <Button kind="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        kind="primary"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                    >
                        {isSubmitting ? 'Creating...' : 'OK'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddProjectModal;
